import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { MSOPractice } from '../../types';
import { RevenueIcon } from '../icons/RevenueIcon';
import { LocationIcon } from '../icons/LocationIcon';
import { PersonIcon } from '../icons/PersonIcon';
import { CardiologyIcon } from '../icons/CardiologyIcon';
import { DermatologyIcon } from '../icons/DermatologyIcon';
import { OrthopedicsIcon } from '../icons/OrthopedicsIcon';
import { GastroenterologyIcon } from '../icons/GastroenterologyIcon';
import { PrimaryCareIcon } from '../icons/PrimaryCareIcon';

interface PracticeCardProps {
  practice: MSOPractice;
  onClick?: () => void;
  variant?: 'default' | 'selected' | 'excluded' | 'target';
}

export const PracticeCard: React.FC<PracticeCardProps> = ({
  practice,
  onClick,
  variant = 'default',
}) => {
  const borderColor = {
    default: 'border-medium-gray',
    selected: 'border-forest-green',
    excluded: 'border-medium-gray opacity-40',
    target: 'border-forest-green border-l-4',
  }[variant];

  const bgColor = variant === 'excluded' ? 'bg-slate-blue opacity-50' : 'bg-slate-blue';

  const SpecialtyIcon = {
    cardiology: CardiologyIcon,
    dermatology: DermatologyIcon,
    orthopedics: OrthopedicsIcon,
    gastroenterology: GastroenterologyIcon,
    'primary-care': PrimaryCareIcon,
  }[practice.specialty];

  return (
    <motion.div
      className={clsx(
        'rounded-lg border-2 p-4 cursor-pointer transition-all',
        'shadow-md hover:shadow-lg',
        borderColor,
        bgColor
      )}
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      layout
    >
      {/* Header */}
      <h3 className="font-bold text-white text-base mb-2">
        {practice.name}
      </h3>

      {/* Details */}
      <div className="space-y-1 text-sm">
        <div className="text-light-gray flex items-center gap-1">
          {SpecialtyIcon && <SpecialtyIcon size={16} />} <span className="capitalize">{practice.specialty}</span>
        </div>

        <div className="text-forest-green font-bold flex items-center gap-1">
          <RevenueIcon size={16} /> ${practice.revenue}M Revenue
        </div>

        <div className="text-light-gray flex items-center gap-1">
          <LocationIcon size={16} /> {practice.location.region}
        </div>

        <div className="text-light-gray flex items-center gap-1">
          <PersonIcon size={16} /> <span className="text-light-gray opacity-70">Age:</span>{' '}
          {practice.characteristics.ownerAge}
        </div>

        <div className="text-light-gray text-xs">
          <span className="text-light-gray opacity-70">Years:</span>{' '}
          {practice.characteristics.yearsEstablished}
        </div>

        {practice.priorityScore && variant === 'target' && (
          <div className="mt-2 pt-2 border-t border-light-gray">
            <div className="text-forest-green font-bold text-sm">
              ⭐ Score: {practice.priorityScore}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};
